import { Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GuideTourButton } from "@/components/guide/GuideButton";
import { keepOpenDuringTour } from "@/components/guide/tour";

import { api } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const Backfill = () => {
  const [st, setSt] = useState(null);
  const [days, setDays] = useState("year");
  const load = () => api.get("/ringover/backfill").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!st?.running) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [st?.running]);
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const start = () => api.post("/ringover/backfill", days === "year" ? { since: yearStart } : { days }).then(({ data }) => { if (data.started) toast.success(`Reprise de l'historique lancée en arrière-plan${data.days ? ` (${data.days} jours à lire)` : ""}.`); else toast.info(data.message || "Une reprise est déjà en cours."); setTimeout(load, 1500); }).catch((e) => toast.error(e?.response?.data?.detail || "Lancement impossible."));
  const pct = st?.days ? Math.round((100 * (st.done || 0)) / (st.days + 1)) : 0;
  return (
    <div className="ck-backfill" data-testid="ck-backfill">
      <h4>Historique des appels</h4>
      {st?.running ? (
        <>
          <div className="cs-progress"><span style={{ width: `${pct}%` }} /><b data-testid="ck-backfill-progress">{pct} % · jour {st.current} · {st.calls} appels lus ({st.new} nouveaux)</b></div>
          <p className="ck-muted">Lecture jour par jour, 2 requêtes/s maximum (limite Ringover). Vous pouvez fermer cette fenêtre.</p>
        </>
      ) : (
        <>
          {st?.finished_at && <p className="ck-muted" data-testid="ck-backfill-last">Dernière reprise : {st.until ? `du ${new Date(st.first_day).toLocaleDateString("fr-FR")} au ${new Date(st.until).toLocaleDateString("fr-FR")}` : `${st.days} jours`}, {st.calls} appels lus, {st.new} nouveaux{st.error ? ` — interrompue : ${st.error}` : ""}.</p>}
          <div className="ck-backfill-row"><select value={days} onChange={(e) => setDays(e.target.value === "year" ? "year" : Number(e.target.value))} data-testid="ck-backfill-days"><option value="year">Compléter depuis le 1er janvier</option><option value={30}>30 derniers jours</option><option value={90}>3 derniers mois</option><option value={180}>6 derniers mois</option><option value={365}>12 derniers mois</option></select>
            <button type="button" className="secondary-button" onClick={start} data-testid="ck-backfill-start">Reprendre l'historique</button></div>
        </>
      )}
    </div>
  );
};

export const RingoverSettings = () => {
  const [agents, setAgents] = useState(null);
  const [rules, setRules] = useState(null);
  const load = () => {
    api.get("/ringover/agents").then(({ data }) => setAgents(data)).catch(() => {});
    api.get("/ringover/tag-rules").then(({ data }) => setRules(data)).catch(() => {});
  };
  const apply = () => api.post("/ringover/agents/apply").then(({ data }) => { toast.success(`${data.updated} appel(s) rattaché(s) · ${data.unmapped_calls} encore sans agent.`); load(); }).catch(fail);
  const fail = (e) => toast.error(e?.response?.data?.detail || "Enregistrement impossible.");
  const mapAgent = (agent, id) => api.put("/ringover/agents", { agent, crm_user_id: id ? Number(id) : null }).then(() => { toast.success("Correspondance enregistrée."); load(); }).catch(fail);
  const toggle = (tag) => {
    const notJoint = rules.not_joint.includes(tag) ? rules.not_joint.filter((t) => t !== tag) : [...rules.not_joint, tag];
    setRules({ ...rules, not_joint: notJoint, joint: rules.tags.filter((t) => !notJoint.includes(t)) });
  };
  const saveRules = () => api.put("/ringover/tag-rules", { joint: rules.joint, not_joint: rules.not_joint }).then(() => toast.success("Règles des tags enregistrées.")).catch(fail);
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild><button type="button" data-testid="ck-ringover-settings"><Settings2 size={14} /> Réglages Ringover</button></DialogTrigger>
      <DialogContent className="ck-settings" onInteractOutside={keepOpenDuringTour} data-testid="ck-ringover-settings-dialog">
        <GuideTourButton slug="ringover-agents" className="secondary-button ck-tour-btn" />
        <DialogHeader><DialogTitle>Réglages Ringover du Cockpit</DialogTitle></DialogHeader>
        <h4>Tags Ringover : client joint ou non ?</h4>
        <p className="ck-muted">Cochez les tags qui veulent dire « client NON joint ». Les autres comptent comme « client joint ».</p>
        <div className="ck-tagrules">{(rules?.tags || []).map((t) => <label key={t} className={rules.not_joint.includes(t) ? "on" : ""}><input type="checkbox" checked={rules.not_joint.includes(t)} onChange={() => toggle(t)} data-testid={`ck-tagrule-${t}`} />{t}</label>)}</div>
        <button type="button" className="primary-button" onClick={saveRules} disabled={!rules} data-testid="ck-tagrules-save">Enregistrer les tags</button>
        <h4>Agents Ringover ↔ utilisateurs CRM</h4>
        <p className="ck-muted">Rattachement automatique par e-mail, puis par nom ; corrigez ici si besoin. {agents ? `${agents.unmapped_calls} appel(s) encore sans agent.` : ""}</p>
        <button type="button" className="secondary-button" onClick={apply} data-testid="ck-agents-apply">Rattacher les appels déjà enregistrés</button>
        <div className="ck-agents">
          {(agents?.agents || []).map((a) => (
            <div key={a.agent} data-testid={`ck-agent-${a.agent}`}>
              <span><strong>{a.name || a.agent}</strong><small className="ck-muted"> {a.email} · {a.calls} appels{a.auto ? " · auto" : a.manual ? " · manuel" : ""}</small></span>
              <select value={a.crm_user_id || ""} onChange={(e) => mapAgent(a.agent, e.target.value)}>
                <option value="">— Non relié —</option>
                {agents.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
          ))}
        </div>
        <Backfill />
      </DialogContent>
    </Dialog>
  );
};
